// src/components/AIPursuitChat.jsx — v3
// AI War Room — SSE streaming (one AI at a time), full markdown rendering

import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import './AIPursuitChat.css';
import { apiFetch } from '../lib/api';

const AI_CONFIG = {
  victor: { name: 'Victor',  icon: '⚡', color: 'var(--dm-indigo)', label: 'Strategy' },
  selma:  { name: 'Selma',   icon: '🔍', color: 'var(--dm-cyan)', label: 'Intelligence' },
  cyrano: { name: 'Cyrano',  icon: '✍️', color: 'var(--dm-purple-vivid)', label: 'Communications' },
  jarvis: { name: 'Jarvis',  icon: '🤖', color: '#64748b', label: 'Orchestration' },
};

function detectAI(message) {
  const m = message.toLowerCase().trim();
  if (m.startsWith('victor'))  return 'Victor';
  if (m.startsWith('selma'))   return 'Selma';
  if (m.startsWith('cyrano'))  return 'Cyrano';
  if (['research','find out','look up','who is','investigate','profile','intel on'].some(k => m.includes(k))) return 'Selma';
  if (['draft','email','outreach','write to','follow up','subject line','cadence'].some(k => m.includes(k))) return 'Cyrano';
  return 'Victor';
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
// Parses markdown into React elements. Handles:
// # ## ### headers, **bold**, *italic*, `code`, ``` code blocks,
// - / * / + bullet lists, 1. numbered lists, > blockquotes, --- dividers, blank lines
function MarkdownContent({ content }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  let key = 0;

  const inlineRender = (text) => {
    // Simple sequential parsing
    let lastIndex = 0;
    const tokens = [];
    const allRe = /(\*\*|__)(.+?)\1|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|`([^`]+)`/g;
    let m;
    while ((m = allRe.exec(text)) !== null) {
      if (m.index > lastIndex) {
        tokens.push({ type: 'text', content: text.slice(lastIndex, m.index) });
      }
      if (m[1]) { // bold
        tokens.push({ type: 'bold', content: m[2] });
      } else if (m[3]) { // italic
        tokens.push({ type: 'italic', content: m[3] });
      } else if (m[4]) { // code
        tokens.push({ type: 'code', content: m[4] });
      }
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) {
      tokens.push({ type: 'text', content: text.slice(lastIndex) });
    }
    if (tokens.length === 0) tokens.push({ type: 'text', content: text });

    return tokens.map((t, idx) => {
      if (t.type === 'bold')   return <strong key={idx}>{t.content}</strong>;
      if (t.type === 'italic') return <em key={idx}>{t.content}</em>;
      if (t.type === 'code')   return <code key={idx} className="apc-inline-code">{t.content}</code>;
      return <span key={idx}>{t.content}</span>;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Blank line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Horizontal rule ---
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={key++} className="apc-hr" />);
      i++;
      continue;
    }

    // Code block ```
    if (line.trim().startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre key={key++} className="apc-code-block">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Headers # ## ###
    const headerMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const text  = headerMatch[2];
      const Tag   = `h${level + 2}`; // h3, h4, h5
      elements.push(<Tag key={key++} className={`apc-h${level}`}>{inlineRender(text)}</Tag>);
      i++;
      continue;
    }

    // Blockquote >
    if (line.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <blockquote key={key++} className="apc-blockquote">
          {quoteLines.map((l, idx) => <p key={idx}>{inlineRender(l)}</p>)}
        </blockquote>
      );
      continue;
    }

    // Bullet list - * +
    if (/^[-*+] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+] /, ''));
        i++;
      }
      elements.push(
        <ul key={key++} className="apc-ul">
          {items.map((item, idx) => <li key={idx}>{inlineRender(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list 1. 2. 3.
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={key++} className="apc-ol">
          {items.map((item, idx) => <li key={idx}>{inlineRender(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Checkbox - [ ] or - [x]
    if (/^- \[[ x]\]/.test(line)) {
      const items = [];
      while (i < lines.length && /^- \[[ x]\]/.test(lines[i])) {
        const checked = lines[i][3] === 'x';
        const text    = lines[i].slice(6);
        items.push({ checked, text });
        i++;
      }
      elements.push(
        <ul key={key++} className="apc-checklist">
          {items.map((item, idx) => (
            <li key={idx} className={`apc-check-item ${item.checked ? 'checked' : ''}`}>
              <span className="apc-check-box">{item.checked ? '✅' : '☐'}</span>
              {inlineRender(item.text)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Regular paragraph
    const paraLines = [];
    while (i < lines.length &&
           lines[i].trim() !== '' &&
           !lines[i].startsWith('#') &&
           !lines[i].startsWith('> ') &&
           !/^[-*+] /.test(lines[i]) &&
           !/^\d+\.\s/.test(lines[i]) &&
           !lines[i].startsWith('```') &&
           !/^---+$/.test(lines[i].trim())) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      elements.push(
        <p key={key++} className="apc-p">
          {paraLines.map((l, idx) => (
            <span key={idx}>
              {inlineRender(l)}
              {idx < paraLines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    }
  }

  return <div className="apc-markdown">{elements}</div>;
}

// ── Streaming animated text (for live typing effect during stream) ─────────────
function StreamingText({ content, isStreaming }) {
  if (!isStreaming) return <MarkdownContent content={content} />;
  // While streaming, show raw text with cursor — markdown renders on completion
  return (
    <span className="apc-streaming-text">
      {content}
      <span className="apc-cursor" />
    </span>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg, isStreaming }) {
  const isUser    = msg.role === 'user';
  const isSystem  = msg.role === 'system';
  const isHandoff = isSystem && (msg.content || '').startsWith('→');
  const aiCfg     = AI_CONFIG[msg.ai] || {};

  if (isHandoff) {
    return (
      <div className="apc-handoff-notice">
        <span>🔀</span>
        <span>{msg.content}</span>
      </div>
    );
  }

  if (isSystem) {
    return (
      <div className="apc-system-notice">
        <span>ℹ️</span>
        <span>{msg.content}</span>
      </div>
    );
  }

  const timeStr = msg.timestamp
    ? new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div
      className={`apc-message ${isUser ? 'apc-message--user' : 'apc-message--ai'}`}
      data-ai={msg.ai || ''}>
      {!isUser && (
        <div className="apc-message-header" style={{ color: aiCfg.color || '#64748b' }}>
          <span className="apc-ai-icon">{aiCfg.icon || '🤖'}</span>
          <span className="apc-ai-name" style={{ color: aiCfg.color }}>{aiCfg.name || msg.ai}</span>
          <span className="apc-ai-label">{aiCfg.label}</span>
          {timeStr && <span className="apc-timestamp">{timeStr}</span>}
        </div>
      )}
      <div className="apc-message-bubble">
        {isUser ? (
          <span>{msg.content}</span>
        ) : (
          <StreamingText content={msg.content || ''} isStreaming={isStreaming} />
        )}
      </div>
      {isUser && timeStr && (
        <div className="apc-message-header apc-message-header--right">
          <span className="apc-timestamp">{timeStr}</span>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const AIPursuitChat = forwardRef(function AIPursuitChat({ context = {}, initialMessage = null, openingBriefing = null }, ref) {
  const [thread,         setThread]         = useState([]);
  const [input,          setInput]          = useState('');
  const [activeAI,       setActiveAI]       = useState(null); // which AI is currently "thinking"
  const [streamingMsgId, setStreamingMsgId] = useState(null); // msg being streamed
  const [loading,        setLoading]        = useState(false);
  const [routingHint,    setRoutingHint]    = useState('Victor');
  const [error,          setError]          = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [learning,       setLearning]       = useState(false);
  const [learnResults,   setLearnResults]   = useState(null);
  const [saved,          setSaved]          = useState(false);
  const [pastSessions,   setPastSessions]   = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);

  const threadEndRef = useRef(null);
  const inputRef     = useRef(null);
  const initFired    = useRef(false);
  const abortRef     = useRef(null);
  const prevOppId    = useRef(context.opportunityId); // track previous opp to detect switches
  const threadRef    = useRef([]);
  const contextRef   = useRef(context);

  // Keep refs in sync for unmount autosave (Phase A.5b)
  useEffect(() => { threadRef.current  = thread;  }, [thread]);
  useEffect(() => { contextRef.current = context; }, [context]);

  // ── Phase A.5b: Autosave on unmount ───────────────────────────────────────
  // If the user navigates away from the War Room without clicking Save or
  // ending via Jarvis, capture the in-progress conversation. Same pattern as
  // TheFloor.jsx. Without this, mid-thread MEDDIC updates, Victor's read,
  // and contacts mentioned are lost when the user switches pages.
  //
  // Uses keepalive:true so the request survives page unload. Fire-and-forget;
  // errors are swallowed because the user is already gone.
  useEffect(() => {
    return () => {
      const t   = threadRef.current  || [];
      const ctx = contextRef.current || {};
      if (!ctx.opportunityId || t.length < 2) return;
      try {
        apiFetch('/api/warroom/learn', {
          method:    'POST',
          headers:   { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            opportunityId: ctx.opportunityId,
            accountId:     ctx.accountId,
            thread:        t,
            context:       ctx,
          }),
        }).catch(() => {});
      } catch {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  // Save current thread when opportunity switches, then reset for the new one
  useEffect(() => {
    const prevId = prevOppId.current;
    const newId  = context.opportunityId;
    if (prevId && newId && prevId !== newId) {
      // Save the thread for the OLD opportunity before switching
      if (thread.length > 0) {
        apiFetch('/api/warroom/sessions', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            opportunityId: prevId,
            accountId:     context.accountId,
            thread,
            context: { ...context, opportunityId: prevId },
          }),
        }).catch(() => {});
      }
      // Reset thread for new opportunity
      setThread([]);
      setSessionsLoaded(false);
      setPastSessions([]);
      setSaved(false);
      setLearnResults(null);
      initFired.current = false;
    }
    prevOppId.current = newId;
  }, [context.opportunityId]); // eslint-disable-line

  useEffect(() => {
    if (initialMessage && !initFired.current && thread.length === 0) {
      initFired.current = true;
      sendMessage(initialMessage);
    }
  }, []); // eslint-disable-line

  // Seed Victor's opening briefing as the first message when the thread is
  // empty. This is a static, deterministic message — no LLM call, no API hit.
  // Parent (OpportunitiesTab) builds the briefing string from already-loaded
  // detail data. Briefing skipped if there's already chat content (re-opens
  // shouldn't repeat the orientation) or if initialMessage is firing (different
  // mode — that's "user typed something on open").
  useEffect(() => {
    if (!openingBriefing) return;
    if (initFired.current) return;
    if (thread.length > 0) return;
    initFired.current = true;
    setThread([{
      role:      'assistant',
      content:   openingBriefing,
      timestamp: new Date().toISOString(),
      ai:        'victor',  // opening briefing is Victor's voice
    }]);
  }, []); // eslint-disable-line

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (e.target.value.trim()) setRoutingHint(detectAI(e.target.value));
  };

  const loadPastSessions = useCallback(async () => {
    if (!context.opportunityId || sessionsLoaded) return;
    try {
      const res  = await apiFetch('/api/warroom/sessions?opportunityId=' + context.opportunityId);
      const data = await res.json();
      if (data.success) { setPastSessions(data.sessions || []); setSessionsLoaded(true); }
    } catch {}
  }, [context.opportunityId, sessionsLoaded]);

  const loadSession = async (sessionId) => {
    setLoadingSession(true);
    try {
      const res  = await apiFetch('/api/warroom/sessions?sessionId=' + sessionId);
      const data = await res.json();
      if (data.success && data.session.thread?.length) {
        setThread(data.session.thread);
        setStreamingMsgId(null);
      }
    } catch {}
    finally { setLoadingSession(false); }
  };

  const saveSession = async (threadToSave) => {
    const t = threadToSave || thread;
    if (!context.opportunityId || !t.length || saving) return;
    setSaving(true);
    try {
      const res  = await apiFetch('/api/warroom/sessions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunityId: context.opportunityId, accountId: context.accountId, thread: t, context }),
      });
      const data = await res.json();
      if (data.success) {
        setSaved(true);
        setSessionsLoaded(false);
        setTimeout(() => setSaved(false), 4000);
        // Auto-learn after save
        setTimeout(() => learnFromSession(threadToSave || thread), 500);
      }
    } catch {}
    finally { setSaving(false); }
  };

  // Extract intelligence from session and write back to Notion records
  const learnFromSession = async (threadToLearn) => {
    const t = threadToLearn || thread;
    if (!t.length || learning) return;
    setLearning(true);
    setLearnResults(null);
    try {
      const res  = await apiFetch('/api/warroom/learn', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunityId: context.opportunityId,
          accountId:     context.accountId,
          thread:        t,
          context,
        }),
      });
      const data = await res.json();
      if (data.success) setLearnResults(data.results || []);
    } catch (err) {
      console.error('[War Room learn]', err.message);
    } finally {
      setLearning(false);
    }
  };

  // Exposed via ref so parent (OpportunitiesTab footer "End Session" button) can
  // trigger a synchronous save + learn cycle and await its completion before
  // closing the workspace. Distinct from saveSession() which auto-cascades to
  // learn after a 500ms timeout (fire-and-forget); endSession awaits both phases
  // sequentially and returns the outcome.
  useImperativeHandle(ref, () => ({
    async endSession() {
      const t   = threadRef.current   || [];
      const ctx = contextRef.current  || {};
      if (!ctx.opportunityId || t.length < 2) {
        return { status: 'empty' };  // nothing to save
      }
      try {
        // Phase 1: persist the session transcript
        const saveRes = await apiFetch('/api/warroom/sessions', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ opportunityId: ctx.opportunityId, accountId: ctx.accountId, thread: t, context: ctx }),
        });
        const saveData = await saveRes.json();
        if (!saveData.success) return { status: 'save_failed', error: saveData.error };

        // Phase 2: distill MEDDIC/PID/business case from the transcript
        const learnRes = await apiFetch('/api/warroom/learn', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ opportunityId: ctx.opportunityId, accountId: ctx.accountId, thread: t, context: ctx }),
        });
        const learnData = await learnRes.json();
        if (!learnData.success) return { status: 'learn_failed', error: learnData.error };

        return { status: 'saved', results: learnData.results || [] };
      } catch (err) {
        return { status: 'error', error: err.message };
      }
    },
  }), []);

  const sendMessage = useCallback(async (messageOverride) => {
    const message = (messageOverride || input).trim();
    if (!message || loading) return;

    setInput('');
    setError(null);
    setLoading(true);
    setActiveAI(null);
    setStreamingMsgId(null);

    // Add user message immediately
    const userMsg = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setThread(prev => [...prev, userMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await apiFetch('/api/jarvis/orchestrate', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        signal:  controller.signal,
        body: JSON.stringify({
          message,
          thread,
          context: {
            type:            context.type || 'opportunity',
            opportunityId:   context.opportunityId,
            opportunityName: context.opportunityName,
            accountId:       context.accountId,
            accountName:     context.accountName,
            stage:           context.stage,
            dealSize:        context.dealSize,
            contacts:        context.contacts || [],
            notes:           context.notes || '',
          },
        }),
      });

      if (!response.ok) throw new Error(`API error ${response.status}`);

      // Read the SSE stream
      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          let event;
          try { event = JSON.parse(raw); } catch { continue; }

          switch (event.type) {

            case 'thinking':
              // An AI is about to respond — show typing indicator
              setActiveAI(event.ai);
              break;

            case 'message': {
              // AI finished its response — add to thread
              const msgId = event.timestamp || Date.now().toString();
              setStreamingMsgId(msgId);
              setActiveAI(null);
              setThread(prev => {
                // Remove any existing thinking placeholder for this AI
                const cleaned = prev.filter(m => !m.thinking);
                return [...cleaned, {
                  role:      'assistant',
                  ai:        event.ai,
                  content:   event.content,
                  timestamp: event.timestamp,
                  id:        msgId,
                }];
              });
              // Clear streaming state after a brief pause (triggers markdown render)
              setTimeout(() => setStreamingMsgId(null), 300);
              break;
            }

            case 'handoff':
            case 'system': {
              // Routing notice or side effect
              setActiveAI(null);
              setThread(prev => [...prev, {
                role:      'system',
                ai:        'jarvis',
                content:   event.content,
                timestamp: event.timestamp || new Date().toISOString(),
              }]);
              break;
            }

            case 'done': {
              // Final sync — replace thread with server's authoritative version
              const finalThread = event.thread;
              if (finalThread) setThread(finalThread);
              setActiveAI(null);
              setStreamingMsgId(null);
              // Auto-save if opportunity context is available
              if (context.opportunityId && finalThread?.length) {
                setTimeout(() => saveSession(finalThread), 800);
              }
              break;
            }

            case 'error':
              throw new Error(event.message || 'Unknown error');

            default:
              break;
          }
        }
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[AIPursuitChat]', err.message);
        setError(err.message);
      }
      setActiveAI(null);
      setStreamingMsgId(null);
    } finally {
      setLoading(false);
      setActiveAI(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, thread, loading, context]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const routingCfg = AI_CONFIG[routingHint.toLowerCase()] || AI_CONFIG.victor;

  const quickPrompts = [
    { label: '⚡ Evaluate this deal',   message: 'Victor, evaluate this opportunity — what is our biggest risk?' },
    { label: '🔍 Research contacts',    message: 'Selma, research the key decision makers at this account' },
    { label: '✍️ Draft outreach',       message: 'Cyrano, draft a signal-led outreach email for this opportunity' },
    { label: '🎯 MEDDIC gaps',          message: 'Victor, identify the gaps in our MEDDIC and recommend next actions' },
  ];

  const isAnyLoading = loading || !!activeAI;

  return (
    <div className="apc-container">
      {/* Header */}
      <div className="apc-header">
        <div className="apc-header-left">
          <span className="apc-header-title">🤖 AI War Room</span>
          <span className="apc-header-sub">Victor · Selma · Cyrano</span>
        </div>
        {thread.length > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {saving  && <span className="apc-status-pill apc-status-saving">⏳ Saving...</span>}
            {learning && <span className="apc-status-pill apc-status-learning">🧠 Learning...</span>}
            {saved && !saving && !learning && (
              <span className="apc-status-pill apc-status-done">✅ Saved & Learned</span>
            )}
            <button className="apc-clear-btn"
              onClick={() => { setThread([]); setError(null); setSaved(false); setActiveAI(null); setLearnResults(null); }}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Context bar */}
      {(context.opportunityName || context.accountName) && (
        <div className="apc-context-bar">
          {context.accountName    && <span className="apc-ctx-item">🏢 {context.accountName}</span>}
          {context.opportunityName && <span className="apc-ctx-item">💼 {context.opportunityName}</span>}
          {context.stage && (
            <span className="apc-ctx-item apc-ctx-stage">
              {context.stage.split(' - ')[1] || context.stage}
            </span>
          )}
        </div>
      )}

      {/* Learning results */}
      {learnResults && learnResults.length > 0 && (
        <div className="apc-learn-results">
          <div className="apc-learn-title">🧠 Intelligence Applied to Platform</div>
          {learnResults.map((r, i) => (
            <div key={i} className="apc-learn-item">{r}</div>
          ))}
          <button className="apc-learn-dismiss" onClick={() => setLearnResults(null)}>✕</button>
        </div>
      )}

      {/* Thread */}
      <div className="apc-thread">
        {thread.length === 0 ? (
          <div className="apc-empty">
            <div className="apc-empty-title">Pursuit Intelligence Room</div>
            <div className="apc-empty-sub">
              Work with Victor, Selma, and Cyrano on this pursuit.
              Start with an AI's name to route directly — or let Jarvis decide.
              AIs respond one at a time, naturally.
            </div>
            <div className="apc-quick-prompts">
              {quickPrompts.map((qp, i) => (
                <button key={i} className="apc-quick-btn"
                  onClick={() => sendMessage(qp.message)} disabled={isAnyLoading}>
                  {qp.label}
                </button>
              ))}
            </div>
            {context.opportunityId && (
              <div className="apc-past-sessions">
                {!sessionsLoaded ? (
                  <button className="apc-load-sessions-btn" onClick={loadPastSessions}>
                    📂 Load Past Sessions
                  </button>
                ) : pastSessions.length === 0 ? (
                  <div className="apc-no-sessions">No saved sessions yet for this pursuit</div>
                ) : (
                  <div className="apc-sessions-list">
                    <div className="apc-sessions-label">Past Sessions</div>
                    {pastSessions.map(s => (
                      <button key={s.id} className="apc-session-item"
                        onClick={() => loadSession(s.id)} disabled={loadingSession}>
                        <div className="apc-session-title">{s.title}</div>
                        {s.summary && <div className="apc-session-summary">{s.summary.slice(0, 100)}...</div>}
                        <div className="apc-session-meta">
                          {s.aisInvolved?.join(' · ')}
                          {s.actionsCreated > 0 && ` · ${s.actionsCreated} actions`}
                          {' · ' + new Date(s.sessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {thread.map((msg, i) => (
              <MessageBubble
                key={msg.id || msg.timestamp || i}
                msg={msg}
                isStreaming={streamingMsgId === (msg.id || msg.timestamp)}
              />
            ))}

            {/* Thinking indicator — shows which AI is currently working */}
            {activeAI && (
              <div className="apc-message apc-message--ai" data-ai={activeAI}>
                <div className="apc-message-header" style={{ color: AI_CONFIG[activeAI]?.color }}>
                  <span className="apc-ai-icon">{AI_CONFIG[activeAI]?.icon}</span>
                  <span className="apc-ai-name" style={{ color: AI_CONFIG[activeAI]?.color }}>
                    {AI_CONFIG[activeAI]?.name}
                  </span>
                  <span className="apc-ai-label">{AI_CONFIG[activeAI]?.label}</span>
                </div>
                <div className="apc-thinking">
                  <span className="apc-thinking-dot" />
                  <span className="apc-thinking-dot" />
                  <span className="apc-thinking-dot" />
                </div>
              </div>
            )}
          </>
        )}
        {error && (
          <div className="apc-error">
            ⚠️ {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}
        <div ref={threadEndRef} />
      </div>

      {/* Input */}
      <div className="apc-input-area">
        <div className="apc-routing-hint" style={{ color: routingCfg.color }}>
          {routingCfg.icon} Routing to: <strong>{routingHint}</strong>
        </div>
        <div className="apc-input-row">
          <textarea
            ref={inputRef}
            className="apc-input"
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Message Victor, Selma, or Cyrano... (Enter to send)"
            rows={2}
            disabled={isAnyLoading}
          />
          <button className="apc-send-btn" onClick={() => sendMessage()}
            disabled={isAnyLoading || !input.trim()}
            style={{ background: routingCfg.color }}>
            {isAnyLoading ? '⏳' : '→'}
          </button>
        </div>
        <div className="apc-input-hints">
          <span>Start with <strong>Victor</strong>, <strong>Selma</strong>, or <strong>Cyrano</strong> to route directly</span>
          <span>AIs respond one at a time — each appears as it finishes</span>
        </div>
      </div>
    </div>
  );
});

export default AIPursuitChat;
